import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { Tabs } from '@/components/tabs/Tabs';
import { Button } from '@/design-system/button';
import {
  ActivityLogTable,
  ConversionOverviewCards,
  StorePerformanceTable,
  TodayClicksAnalytics,
  useActivityLog,
  useConversionAnalyticsOverView,
  useStorePerformance,
} from '@/features/dashboard/conversionAnalytics';
import SelectStoreModal from '@/features/dashboard/conversionAnalytics/components/SelectStoreModal';
import {
  clearStoredStoreNames,
  conversionAnalyticsSearchSchema,
  getStoredStoreName,
  storeStoreName,
} from '@/features/dashboard/conversionAnalytics/conversionAnalyticsState';
import { useDynamicLocale } from '@/hoslog-intl';
import { createFileRoute, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { ChevronDown, X } from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/(app)/dashboard/conversion-analytics/')({
  component: RouteComponent,

  validateSearch: (search: SearchSchemaInput) => {
    return conversionAnalyticsSearchSchema.parse(search);
  },
});

function RouteComponent() {
  const { activeTab } = useSearch({ from: '/(app)/dashboard/conversion-analytics/' });
  const { openConfirmation } = useConfirmationStore();
  const [activeTabProp, setActiveTabProp] = useQueryState('activeTab', parseAsString.withDefault('overview'));
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(0));
  const [, setCreatedDate] = useQueryState('createdDate', parseAsString);
  const [, setSearchQuery] = useQueryState('q', parseAsString);

  const { t } = useDynamicLocale();
  const [activeStore, setActiveStore] = useQueryState('clubId', parseAsString.withDefault('all'));
  const [legacyStoreName, setLegacyStoreName] = useQueryState('storeName', parseAsString);
  const [selectedStoreName, setSelectedStoreName] = useState(() => getStoredStoreName(activeStore) ?? legacyStoreName);
  const [channel, setChannel] = useQueryState('channel', parseAsString.withDefault('all'));
  const { conversionAnalyticsOverview, isLoading: isOverviewLoading } = useConversionAnalyticsOverView({
    clubId: activeStore === 'all' ? undefined : activeStore,
    channel,
  });
  const { storePerformance, isLoading: isStorePerformanceLoading, pagination } = useStorePerformance();
  const { activityLogs, isLoading: isActivityLogLoading, pagination: activityLogPagination } = useActivityLog();
  const [draftStore, setDraftStore] = useState(activeStore);
  const draftStoreRef = useRef(draftStore);
  const draftStoreNameRef = useRef(selectedStoreName);
  const activeStoreName =
    activeStore === 'all'
      ? t('dashboard.conversion.page.allStores')
      : (selectedStoreName ?? storePerformance.find(({ clubId }) => clubId === activeStore)?.storeName ?? activeStore);

  // keep ref in sync whenever draft changes
  useEffect(() => {
    draftStoreRef.current = draftStore;
  }, [draftStore]);

  useEffect(() => {
    if (!legacyStoreName || activeStore === 'all') return;

    storeStoreName(activeStore, legacyStoreName);
    setSelectedStoreName(legacyStoreName);
    void setLegacyStoreName(null);
  }, [activeStore, legacyStoreName, setLegacyStoreName]);

  useEffect(() => {
    if (legacyStoreName) return;
    setSelectedStoreName(getStoredStoreName(activeStore));
  }, [activeStore, legacyStoreName]);

  const onTabChange = (tab: string): void => {
    void Promise.all([setActiveTabProp(tab), setPage(0), setCreatedDate(null), setSearchQuery(null)]);
  };

  const clearActiveStore = () => {
    setDraftStore('all');
    draftStoreRef.current = 'all';
    draftStoreNameRef.current = null;
    clearStoredStoreNames();
    setSelectedStoreName(null);
    void Promise.all([setActiveStore('all'), setLegacyStoreName(null), setPage(0)]);
  };

  const tabItems = [
    {
      label: t('dashboard.conversion.tabs.overview'),
      value: 'overview',
      content: (
        <div className="flex flex-col gap-6 mt-4">
          <ConversionOverviewCards data={conversionAnalyticsOverview?.summary} isLoading={isOverviewLoading} />
          <TodayClicksAnalytics
            data={conversionAnalyticsOverview}
            channel={channel}
            onChannelChange={(value) => void setChannel(value)}
            isLoading={isOverviewLoading}
          />
          <StorePerformanceTable
            data={storePerformance}
            isLoading={isStorePerformanceLoading}
            pagination={pagination}
          />
        </div>
      ),
    },
    {
      label: t('dashboard.conversion.tabs.activityLog'),
      value: 'activity-log',
      content: (
        <div className="mt-4">
          <ActivityLogTable data={activityLogs} isLoading={isActivityLogLoading} pagination={activityLogPagination} />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="relative">
        <div className="absolute right-0 -top-[66px] flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setDraftStore(activeStore);
              draftStoreRef.current = activeStore;
              draftStoreNameRef.current = selectedStoreName;
              openConfirmation({
                title: t('dashboard.conversion.modal.selectStore'),
                actionLabel: t('common.page.modal.submit'),
                cancelLabel: '',
                size: 'lg',
                content: (
                  <SelectStoreModal
                    initialSelected={activeStore}
                    onSelect={(clubId, storeName) => {
                      setDraftStore(clubId);
                      draftStoreNameRef.current = storeName;
                    }}
                  />
                ),
                onAction: () => {
                  const storeName = draftStoreNameRef.current;
                  if (storeName) {
                    storeStoreName(draftStoreRef.current, storeName);
                  }
                  setSelectedStoreName(storeName);
                  void Promise.all([setActiveStore(draftStoreRef.current), setLegacyStoreName(null), setPage(0)]);
                },
                onCancel: () => {},
              });
            }}
          >
            {activeStoreName}
            <ChevronDown />
          </Button>
          {activeStore !== 'all' && (
            <Button variant="outline" size="icon" aria-label="Clear selected store" onClick={clearActiveStore}>
              <X />
            </Button>
          )}
        </div>
      </div>
      <Tabs
        listClassName="bg-muted"
        triggerClassName="bg-muted rounded-lg px-6 py-3"
        defaultActive={activeTab ?? activeTabProp}
        items={tabItems}
        onChange={onTabChange}
      />
    </>
  );
}
